import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import debounce from "lodash/debounce";
import { Radio, RadioGroup } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
} from "catalyst-ui-kit/dialog";
import { Button } from "catalyst-ui-kit/button";
import { Input } from "catalyst-ui-kit/input";
import ConfirmButton from "./ConfirmButton";
import {
  fetchParentCandidates,
  setFileParent,
} from "services/upload-file.service";
import { vilniusYmd } from "utils/vilnius-date";

// A search needs two characters (the backend ignores shorter ones and then
// returns the suggestions). Below that the picker keeps showing the suggestions.
const SEARCH_MIN_LENGTH = 2;
const DEBOUNCE_DELAY = 300;

interface ParentCandidate {
  id: string;
  name: string;
  date?: string | null;
  categoryNames: string[];
  customerNames: string[];
}

export interface AttachToDocumentResult {
  ok: string[];
  failed: { id: string; message: string }[];
}

interface AttachToDocumentModalProps {
  companyId: string;
  fileIds: string[];
  /** Optional display names by file id, used to say which files failed. */
  fileLabels?: Record<string, string>;
  open: boolean;
  onClose: () => void;
  onDone: (result: AttachToDocumentResult) => void;
}

const optionCls =
  "group flex min-h-11 cursor-pointer items-start gap-3 border-b border-zinc-950/5 px-4 py-3 last:border-b-0 " +
  "hover:bg-zinc-950/5 data-[checked]:bg-blue-50 data-[focus]:bg-zinc-950/5";
const dotCls =
  "mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border border-zinc-400 " +
  "group-data-[checked]:border-blue-600 group-data-[checked]:bg-blue-600";

const noticeCls = "p-4 text-sm text-zinc-500";

/**
 * Picks a MAIN document (no parent of its own) and attaches one or more
 * files to it via PATCH /files/:id/parent. Used both from the document list
 * bulk toolbar and from a single document's own page.
 *
 * The candidates come from GET /files/:id/parent-candidates and are shown as
 * soon as the dialog opens: the newest documents of the file's customer (or
 * category). With several files selected the suggestions follow the FIRST
 * file; searching works the same for all of them.
 */
export default function AttachToDocumentModal({
  companyId,
  fileIds,
  fileLabels,
  open,
  onClose,
  onDone,
}: AttachToDocumentModalProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [candidates, setCandidates] = useState<ParentCandidate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [failures, setFailures] = useState<{ id: string; message: string }[]>(
    []
  );

  // The files this dialog works on are snapshotted when it opens: the caller
  // may change its selection while the dialog stays open (bulk mode), and
  // after a partial failure only the files that did not move are retried.
  const [pendingIds, setPendingIds] = useState<string[]>([]);
  const [initialIds, setInitialIds] = useState<string[]>([]);
  const [labels, setLabels] = useState<Record<string, string>>({});
  // Numbers every request when it is made (not when its debounce fires), so an
  // older or slower response never overwrites the list of the query the user is
  // looking at now, nor one from before the dialog was closed and reopened.
  const requestSeqRef = useRef(0);
  // The text the list on screen was asked for ("" = the suggestions).
  const requestedTextRef = useRef("");

  // Loads the candidates for `searchText` ("" = suggestions) for request number
  // `seq`. The files being attached are left out of the list (bulk mode could
  // offer a file as its own parent otherwise).
  const loadCandidates = useCallback(
    async (searchText: string, attachIds: string[], seq: number) => {
      try {
        const { candidates: found } = await fetchParentCandidates(
          companyId,
          attachIds[0],
          searchText
        );
        if (seq !== requestSeqRef.current) return;
        setCandidates(
          (found || []).filter((c: ParentCandidate) => !attachIds.includes(c.id))
        );
        setLoadFailed(false);
      } catch (e) {
        if (seq !== requestSeqRef.current) return;
        setCandidates([]);
        setLoadFailed(true);
      }
      setIsLoading(false);
    },
    [companyId]
  );
  const loadCandidatesDebounced = useMemo(
    () => debounce(loadCandidates, DEBOUNCE_DELAY),
    [loadCandidates]
  );
  useEffect(() => () => loadCandidatesDebounced.cancel(), [loadCandidatesDebounced]);

  // Reset the whole picker only on the closed -> open transition. In bulk mode
  // the caller's onDone clears the selection (changing `fileIds`) while the
  // dialog is still open to show per-file failures — resetting on that too
  // would wipe the failure list the user is meant to see.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (!open && wasOpenRef.current) {
      // A search still waiting for its pause, or an answer still on its way,
      // must not reach the next opening.
      loadCandidatesDebounced.cancel();
      requestSeqRef.current += 1;
    }
    if (open && !wasOpenRef.current) {
      requestedTextRef.current = "";
      setPendingIds(fileIds);
      setInitialIds(fileIds);
      setLabels(fileLabels || {});
      setQuery("");
      setCandidates([]);
      setSelectedId(null);
      setFailures([]);
      setLoadFailed(false);
      if (companyId && fileIds.length) {
        setIsLoading(true);
        loadCandidates("", fileIds, requestSeqRef.current);
      }
    }
    wasOpenRef.current = open;
    // Snapshot only on open — see the pendingIds comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleQueryChange = (value: string) => {
    setQuery(value);
    const trimmed = value.trim();
    const searchText = trimmed.length >= SEARCH_MIN_LENGTH ? trimmed : "";
    if (searchText === requestedTextRef.current) return;

    requestedTextRef.current = searchText;
    requestSeqRef.current += 1;
    setSelectedId(null);
    setIsLoading(true);
    loadCandidatesDebounced(searchText, initialIds, requestSeqRef.current);
  };

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const handleConfirm = async () => {
    if (!selectedId || !companyId || !pendingIds.length || isSubmitting) return;
    setIsSubmitting(true);
    setFailures([]);
    const ok: string[] = [];
    const failed: { id: string; message: string }[] = [];
    for (const fileId of pendingIds) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await setFileParent(companyId, fileId, { parentId: selectedId });
        ok.push(fileId);
      } catch (e: any) {
        failed.push({
          id: fileId,
          message: e?.response?.data?.message || "error",
        });
      }
    }
    setIsSubmitting(false);
    setPendingIds(failed.map((f) => f.id));
    onDone({ ok, failed });
    if (failed.length === 0) {
      onClose();
    } else {
      // Keep the picker open so the user can see exactly which files did
      // not move (the rest already did — the caller's onDone already
      // refreshed the list for those) and retry just those, e.g. with
      // another parent.
      setFailures(failed);
    }
  };

  const trimmedQuery = query.trim();
  const hasSearchText = trimmedQuery.length >= SEARCH_MIN_LENGTH;
  const isTypingTooLittle = trimmedQuery.length > 0 && !hasSearchText;
  // A phone would put the keyboard over the list that is the point of the dialog.
  const focusSearchOnOpen = !window.matchMedia?.("(pointer: coarse)")?.matches;

  return (
    <Dialog open={open} onClose={handleClose} size="lg">
      <DialogTitle>{t("attach_to_document")}</DialogTitle>
      <DialogBody>
        <Input
          type="text"
          value={query}
          onChange={(e) => handleQueryChange(e.target.value)}
          placeholder={t("attach_to_document_search_placeholder")}
          autoFocus={focusSearchOnOpen}
        />
        {isTypingTooLittle && (
          <p className="mt-2 text-sm text-zinc-500">
            {t("attach_to_document_type_more")}
          </p>
        )}
        {!hasSearchText && !isLoading && candidates.length > 0 && (
          <p className="mt-4 text-xs font-medium text-zinc-500">
            {t("attach_to_document_suggestions")}
          </p>
        )}
        <div className="mt-2 max-h-[min(20rem,45dvh)] overflow-y-auto rounded-lg border border-zinc-950/10">
          {isLoading ? (
            <div className={noticeCls}>{t("loading")}</div>
          ) : loadFailed ? (
            <div className={noticeCls}>{t("attach_to_document_load_failed")}</div>
          ) : candidates.length === 0 ? (
            <div className={noticeCls}>{t("attach_to_document_none_found")}</div>
          ) : (
            <RadioGroup
              value={selectedId}
              onChange={setSelectedId}
              aria-label={t("attach_to_document")}
            >
              {candidates.map((candidate) => (
                <Radio key={candidate.id} value={candidate.id} className={optionCls}>
                  <span className={dotCls}>
                    <span className="size-2 rounded-full bg-white opacity-0 group-data-[checked]:opacity-100" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block break-words text-sm font-medium text-zinc-950">
                      {candidate.name}
                    </span>
                    <span className="mt-0.5 block break-words text-xs text-zinc-500">
                      {[
                        vilniusYmd(candidate.date),
                        candidate.categoryNames.join(", "),
                        candidate.customerNames.join(", "),
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </span>
                </Radio>
              ))}
            </RadioGroup>
          )}
        </div>
        {failures.length > 0 && (
          <div className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950/50 dark:text-red-300">
            <p className="font-medium">
              {t("attach_to_document_failures_title")}
            </p>
            <ul className="mt-1 list-disc pl-5">
              {failures.map((failure) => (
                <li key={failure.id}>
                  {labels[failure.id] ? `${labels[failure.id]}: ` : ""}
                  {t(failure.message)}
                </li>
              ))}
            </ul>
          </div>
        )}
      </DialogBody>
      <DialogActions>
        <Button plain className="min-h-11" onClick={handleClose} disabled={isSubmitting}>
          {t("cancel")}
        </Button>
        <ConfirmButton
          onClick={handleConfirm}
          disabled={!selectedId || !pendingIds.length || isSubmitting}
        >
          {t("attach_to_document_confirm")}
        </ConfirmButton>
      </DialogActions>
    </Dialog>
  );
}
